/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';

import logoMyshop from '../../assets/logo-myshop.png';
import { COMPANY, INTERNAL_DOC_NOTICE } from '../../config/company';
import {
   AuthSlot,
   CutLine,
   DataBox,
   DocFooter,
   DocHeader,
   DocStatus,
   HalfArea,
   IdentityBand,
   ItemsTable,
   NotesBox,
   Sheet,
   SheetBody,
   SignatureArea,
   SignatureLine,
   VoidWatermark,
} from './styled';

// ===========================================================================
// O comprovante impresso, na ordem de blocos do handoff de design:
//   1. cabeçalho (marca + identidade da empresa · eyebrow + título do tipo)
//   2. faixa de identidade (#373737)
//   3. dados do movimento (4 colunas + descrição)
//   4. tabela de itens (thead escuro, zebra, total no tfoot)
//   5. [folha inteira: observações] assinaturas + slot de autenticação
//   6. rodapé (emitido por/em · empresa · aviso fiscal)
//
// **Sempre A4 retrato** (decisão do Caio, 2026-09-13). O que muda é a área:
//
//   `full = false` → MEIA FOLHA: o documento ocupa a metade de cima, com
//                    altura fixa, e uma LINHA DE RECORTE no meio exato da
//                    folha. O operador corta e guarda a metade limpa — uma
//                    folha a menos na próxima impressão.
//   `full = true`  → FOLHA INTEIRA: régua ~15% maior e o campo de
//                    observações da conferência.
//
// Um conjunto de dados só, como o contrato exige — só o layout muda.
// ===========================================================================

// Título do documento e rótulos de assinatura por tipo de movimento.
//
// ⚠️ `transferencia` NÃO é um tipo de movimento no sistema — é um `in`/`out`
// com vínculo de transferência (campo `transfer`). E os rótulos do handoff
// ("Responsável pela expedição" / "Conferente / transportador") descrevem a
// PERNA DE SAÍDA: na entrada no destino quem assina está recebendo, não
// expedindo. Por isso a transferência se divide pelo `transfer.role`.
// O título traz SÓ o tipo do movimento (ajuste do Caio, 2026-09-13): a
// palavra "Comprovante" já está no eyebrow logo acima ("Comprovante de
// movimentação de estoque"), e repeti-la na linha seguinte era eco.
const DOC_KIND = {
   entrada: {
      title: 'Entrada',
      left: 'Responsável pela entrega no estoque',
      right: 'Conferente de estoque',
   },
   saida: {
      title: 'Saída',
      left: 'Responsável pela retirada do estoque',
      right: 'Conferente de estoque',
   },
   transfer_out: {
      title: 'Transferência — expedição',
      left: 'Responsável pela expedição',
      right: 'Conferente / transportador',
   },
   transfer_in: {
      title: 'Transferência — recebimento',
      left: 'Responsável pelo recebimento no destino',
      right: 'Conferente de estoque',
   },
   transfer_reversion: {
      title: 'Transferência — estorno',
      left: 'Responsável pelo estorno',
      right: 'Conferente de estoque',
   },
};

export function resolveDocKind(moveType, transferRole) {
   if (transferRole) {
      return DOC_KIND[`transfer_${transferRole}`] ?? DOC_KIND.transfer_out;
   }
   return moveType === 'out' ? DOC_KIND.saida : DOC_KIND.entrada;
}

export default function ReceiptDocument({
   full,
   movementId,
   statusLabel,
   isVoid = false,
   moveType,
   transferRole = null,
   pdvLabel,
   userLabel,
   docType,
   docNumber,
   dateTime,
   description,
   items,
   totalUnits,
   cancellation = null,
   issuedBy,
   issuedAt,
}) {
   const kind = resolveDocKind(moveType, transferRole);

   const docLabel = [docType, docNumber && `nº ${docNumber}`]
      .filter(Boolean)
      .join(' ');

   // Só CNPJ (+ endereço da filial quando houver contrato). A IE saiu do
   // documento por decisão (config/company.js explica), e o endereço nunca
   // imprime placeholder.
   const companyIds = [`CNPJ ${COMPANY.cnpj}`, COMPANY.branchAddress]
      .filter(Boolean)
      .join(' · ');

   // Os blocos são os mesmos nas duas áreas; só a meia folha os envolve numa
   // caixa de altura fixa, para o recorte cair no meio da FOLHA e não onde o
   // conteúdo terminar.
   const content = (
      <>
         {/* 1. Cabeçalho */}
         <DocHeader $full={full}>
            <div className="brand">
               {/* alt vazio de propósito: a razão social vem ao lado, no
                   texto — o leitor de tela não deve ouvir a marca duas vezes. */}
               <img className="mark" src={logoMyshop} alt="" />
               <div className="legal">
                  <div className="name">{COMPANY.legalName}</div>
                  <div className="ids">{companyIds}</div>
               </div>
            </div>

            <div className="titles">
               <div className="eyebrow">
                  Comprovante de movimentação de estoque
               </div>
               <div className="title">{kind.title}</div>
            </div>
         </DocHeader>

         {/* 2. Faixa de identidade */}
         <IdentityBand $full={full}>
            <div className="left">
               Movimento nº {movementId}
               {docLabel ? ` · ${docLabel}` : ''}
               {pdvLabel ? ` · ${pdvLabel}` : ''}
            </div>
            <div className="right">{dateTime}</div>
         </IdentityBand>

         {/* 3. Dados do movimento */}
         <DataBox $full={full}>
            <div className="grid">
               <div className="cell">
                  <div className="cap">ID do movimento</div>
                  <div className="val">#{movementId}</div>
               </div>
               <div className="cell">
                  <div className="cap">Situação</div>
                  <div className="val">
                     <DocStatus $variant={isVoid ? 'void' : 'default'}>
                        {statusLabel}
                     </DocStatus>
                  </div>
               </div>
               <div className="cell">
                  <div className="cap">Usuário responsável</div>
                  <div className="val">{userLabel || '—'}</div>
               </div>
               <div className="cell">
                  <div className="cap">Itens</div>
                  <div className="val">
                     {items.length} {items.length === 1 ? 'item' : 'itens'} ·{' '}
                     {totalUnits} un.
                  </div>
               </div>
            </div>

            <div className="desc">
               <div className="cap">Descrição</div>
               <div className="val">{description || '—'}</div>
            </div>

            {/* Cancelamento (handoff 0095) — só em movimento excluído. O
                design não previu este caso; entra como continuação da caixa
                de dados, na mesma hairline. */}
            {!!cancellation && (
               <div className="desc">
                  <div className="cap">Exclusão</div>
                  <div className="val">
                     {cancellation.reason || 'Motivo não informado'}
                     {cancellation.at ? ` · ${cancellation.at}` : ''}
                     {cancellation.by ? ` · por ${cancellation.by}` : ''}
                  </div>
               </div>
            )}
         </DataBox>

         {/* 4. Tabela de itens */}
         <ItemsTable $full={full}>
            <thead>
               <tr>
                  <th className="idx">#</th>
                  <th>Cód.</th>
                  <th>Descrição do produto</th>
                  <th className="center">Un.</th>
                  <th className="num">Qtd.</th>
               </tr>
            </thead>
            <tbody>
               {items.map((item, index) => (
                  <tr key={item.key}>
                     <td className="idx">{index + 1}</td>
                     <td className="code">{item.sku || '—'}</td>
                     <td>{item.name}</td>
                     <td className="center">{item.unit}</td>
                     <td className="num">{item.qty}</td>
                  </tr>
               ))}
            </tbody>
            <tfoot>
               <tr>
                  <td colSpan={3}>Total de itens: {items.length}</td>
                  <td className="center">Total qtd.</td>
                  <td className="num">{totalUnits}</td>
               </tr>
            </tfoot>
         </ItemsTable>

         {/* 5. Observações (só folha inteira) + assinaturas + autenticação */}
         {/* Espaço livre para a conferência escrever à mão — SEM linhas
             pautadas (ajuste do Caio, 2026-09-13) e com metade da altura
             anterior, que é o porte suficiente. */}
         {full && (
            <NotesBox>
               <div className="cap">Observações da conferência</div>
               <div className="space" />
            </NotesBox>
         )}

         <SignatureArea $full={full}>
            <SignatureLine $full={full}>
               <div className="rule" />
               <div className="role">{kind.left}</div>
               <div className="hint">
                  {full
                     ? 'Nome legível, matrícula e data'
                     : 'Nome, matrícula e data'}
               </div>
            </SignatureLine>

            <SignatureLine $full={full}>
               <div className="rule" />
               <div className="role">{kind.right}</div>
               <div className="hint">
                  {full
                     ? 'Nome legível, matrícula e data'
                     : 'Nome, matrícula e data'}
               </div>
            </SignatureLine>

            {/* Espaço RESERVADO da autenticação (QR + código de verificação +
                código de barras). Vazio por decisão: o código só vale quando
                houver hash persistido e consulta pública. O slot existe para
                essa fase entrar sem reposicionar as assinaturas. */}
            {!full && <AuthSlot $full={full} aria-hidden="true" />}
         </SignatureArea>

         {full && <AuthSlot $full={full} aria-hidden="true" />}

         {/* 6. Rodapé — só o carimbo de emissão e o aviso fiscal. A
             identidade da empresa saiu daqui (ajuste do Caio, 2026-09-13):
             ela já está no cabeçalho, e repetir razão social + CNPJ no pé
             fazia o rodapé quebrar em duas linhas sem informar nada novo. */}
         <DocFooter $full={full}>
            <div className="emit">
               Emitido por {issuedBy} em {issuedAt}
            </div>
            <div className="notice">{INTERNAL_DOC_NOTICE}</div>
         </DocFooter>
      </>
   );

   return (
      <Sheet data-print-root>
         {isVoid && (
            <VoidWatermark $full={full} aria-hidden="true">
               <span>EXCLUÍDO</span>
            </VoidWatermark>
         )}

         <SheetBody $full={full}>
            {full ? (
               content
            ) : (
               <>
                  <HalfArea>{content}</HalfArea>

                  {/* Linha de recorte: o documento ocupou meia folha, então a
                      metade de baixo sai limpa e pode ser reaproveitada. */}
                  <CutLine aria-hidden="true">
                     <span className="rule" />
                     <span className="label">Recorte aqui</span>
                     <span className="rule" />
                  </CutLine>
               </>
            )}
         </SheetBody>
      </Sheet>
   );
}

ReceiptDocument.propTypes = {
   // false = meia folha com linha de recorte; true = folha inteira.
   full: PropTypes.bool.isRequired,
   movementId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
   statusLabel: PropTypes.string.isRequired,
   isVoid: PropTypes.bool,
   moveType: PropTypes.string,
   // 'out' | 'in' | 'reversion' quando o movimento vem de uma transferência.
   transferRole: PropTypes.string,
   pdvLabel: PropTypes.string,
   userLabel: PropTypes.string,
   docType: PropTypes.string,
   docNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   dateTime: PropTypes.string,
   description: PropTypes.string,
   items: PropTypes.arrayOf(
      PropTypes.shape({
         key: PropTypes.string.isRequired,
         sku: PropTypes.string,
         name: PropTypes.string,
         unit: PropTypes.string,
         qty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })
   ).isRequired,
   totalUnits: PropTypes.number.isRequired,
   cancellation: PropTypes.shape({
      reason: PropTypes.string,
      at: PropTypes.string,
      by: PropTypes.string,
   }),
   issuedBy: PropTypes.string.isRequired,
   issuedAt: PropTypes.string.isRequired,
};
